import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Badge, Button, Center, Heading, HStack, Progress, Text, VStack } from '@chakra-ui/react';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { useOpportunityCounts } from '@src/components/sco/Advisors/hooks/useOpportunityCounts';
import { AdvisorsSubNavLayout } from '@src/components/sco/Advisors/layout/AdvisorsSubNavLayout';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { advisorSupplyReadinessScore, advisorSupplyReadinessSteps } from '@src/components/sco/Advisors/utils/advisorSupplyReadiness';
import { opportunitiesNavItems, OpportunitiesSectionKey } from '@src/components/sco/Advisors/utils/opportunitiesNav';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { MY_ADVISOR_PROFILE } from '@src/gql/queries/MyAdvisorProfile';
import { ScoAdvisorProfile } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { ReactNode } from 'react';
type MyProfileData = {
  myAdvisorProfile?: ScoAdvisorProfile | null;
};

/**
 * Opportunities shell. Adds the readiness meter and badge counts on top of the
 * shared Advisors sub-nav layout.
 */
export const OpportunitiesLayout = t0 => {
  const $ = _c(34);
  const {
    activeKey,
    title,
    description,
    children
  } = t0;
  const paths = useAdvisorsPaths();
  const {
    currentUser
  } = useCurrentUser();
  const {
    counts
  } = useOpportunityCounts();
  const navItems = opportunitiesNavItems(paths);
  let t1;
  if ($[0] !== activeKey) {
    t1 = item => item.key === activeKey;
    $[0] = activeKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const activeItem = navItems.find(t1);
  useAdvisorsToolbar({
    breadcrumbs: activeKey === "overview" ? advisorsBreadcrumbsWithTrail({
      title: "Opportunities",
      href: paths.opportunities
    }) : advisorsBreadcrumbsWithTrail({
      title: "Opportunities",
      href: paths.opportunities
    }, {
      title: activeItem?.title ?? "Opportunities",
      href: activeItem?.href ?? paths.opportunities
    })
  });
  const t2 = !currentUser?.userId;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      skip: t2,
      fetchPolicy: "cache-and-network"
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const profileQuery = useQuery(MY_ADVISOR_PROFILE, t3);
  let t4;
  if ($[4] !== currentUser?.pictureUrl || $[5] !== currentUser?.shortBio || $[6] !== profileQuery.data?.myAdvisorProfile) {
    const readinessSteps = advisorSupplyReadinessSteps(profileQuery.data?.myAdvisorProfile, {
      pictureUrl: currentUser?.pictureUrl,
      shortBio: currentUser?.shortBio
    });
    t4 = advisorSupplyReadinessScore(readinessSteps);
    $[4] = currentUser?.pictureUrl;
    $[5] = currentUser?.shortBio;
    $[6] = profileQuery.data?.myAdvisorProfile;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const readiness = t4;
  const readinessPalette = readiness.percent >= 100 ? "green" : "orange";
  const T0 = AdvisorsSubNavLayout;
  const t5 = "Invites, applications, matched seats, and the boards you already advise.";
  const t6 = "Opportunities";
  let t7;
  if ($[8] !== counts) {
    t7 = item_0 => ({
      key: item_0.key,
      title: item_0.title,
      href: item_0.href,
      icon: item_0.icon,
      count: item_0.countKey ? counts[item_0.countKey] : undefined
    });
    $[8] = counts;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = navItems.map(t7);
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Text color="colorPalette.fg/60" textStyle="labelXs">Ready to advise</Text>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== readiness.done || $[12] !== readiness.total || $[13] !== readinessPalette) {
    t10 = <HStack gap={2} justify="space-between">{t9}<Badge colorPalette={readinessPalette} paddingX={2} paddingY={1} size="xs" variant="subtle">{readiness.done}/{readiness.total}</Badge></HStack>;
    $[11] = readiness.done;
    $[12] = readiness.total;
    $[13] = readinessPalette;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Progress.Track><Progress.Range /></Progress.Track>;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== readiness.percent || $[17] !== readinessPalette) {
    t12 = <Progress.Root colorPalette={readinessPalette} size="xs" value={readiness.percent}>{t11}</Progress.Root>;
    $[16] = readiness.percent;
    $[17] = readinessPalette;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== paths.ownProfile || $[20] !== readiness.percent) {
    t13 = readiness.percent < 100 && <Button asChild={true} size="xs" variant="outline"><NextLink href={paths.ownProfile}>Finish setup</NextLink></Button>;
    $[19] = paths.ownProfile;
    $[20] = readiness.percent;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t10 || $[23] !== t12 || $[24] !== t13) {
    t14 = <VStack align="stretch" bg="colorPalette.solid/25" borderRadius="md" gap={2} padding={3}>{t10}{t12}{t13}</VStack>;
    $[22] = t10;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== T0 || $[27] !== activeKey || $[28] !== children || $[29] !== description || $[30] !== t14 || $[31] !== t8 || $[32] !== title) {
    t15 = <T0 activeKey={activeKey} description={description} railDescription={t5} railTitle={t6} title={title} items={t8} railFooter={t14}>{children}</T0>;
    $[26] = T0;
    $[27] = activeKey;
    $[28] = children;
    $[29] = description;
    $[30] = t14;
    $[31] = t8;
    $[32] = title;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
};

/** Shared gate: Opportunities is advisor-supply only. */
export const OpportunitiesAccessGate = t0 => {
  const $ = _c(14);
  const {
    children
  } = t0;
  const paths = useAdvisorsPaths();
  const {
    currentUser
  } = useCurrentUser();
  if (!currentUser) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Center height="full" padding={8}><Text>Sign in to view advisor opportunities.</Text></Center>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!currentUser.advisorProfile?.isApproved) {
    const isPending = currentUser.advisorProfile?.statusKey === "applied";
    const t1 = isPending ? "Application in review" : "Become an advisor first";
    let t2;
    if ($[1] !== t1) {
      t2 = <Heading size="lg">{t1}</Heading>;
      $[1] = t1;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const t3 = isPending ? "Opportunities unlock after an admin approves your profile. Members on the $99+ plan go live immediately on submit." : "Apply to advise on Startups.com to unlock invites and matched open seats from founders.";
    let t4;
    if ($[3] !== t3) {
      t4 = <Text color="colorPalette.fg/60" fontSize="sm">{t3}</Text>;
      $[3] = t3;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    const t5 = isPending ? "View application" : "Apply to advise";
    let t6;
    if ($[5] !== paths.apply || $[6] !== t5) {
      t6 = <Button asChild={true} variant="cta"><NextLink href={paths.apply}>{t5}</NextLink></Button>;
      $[5] = paths.apply;
      $[6] = t5;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== t2 || $[9] !== t4 || $[10] !== t6) {
      t7 = <Center height="full" padding={8}><VStack gap={3} maxWidth="md" textAlign="center">{t2}{t4}{t6}</VStack></Center>;
      $[8] = t2;
      $[9] = t4;
      $[10] = t6;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  let t1;
  if ($[12] !== children) {
    t1 = <>{children}</>;
    $[12] = children;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  return t1;
};